/**
 * TanStack Query hooks for Scheduler Jobs API
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut } from "@/lib/api-client";
import type { JobRun, JobRunResponse, SchedulerJob, UpdateJobRequest } from "@/types/api/settings";

// ============================================================================
// Query Keys
// ============================================================================

export const SCHEDULER_QUERY_KEYS = {
    all: ["scheduler-jobs"] as const,
    lists: () => [...SCHEDULER_QUERY_KEYS.all, "list"] as const,
    list: () => [...SCHEDULER_QUERY_KEYS.lists()] as const,
    details: () => [...SCHEDULER_QUERY_KEYS.all, "detail"] as const,
    detail: (jobId: string) => [...SCHEDULER_QUERY_KEYS.details(), jobId] as const,
    history: (jobId: string) => [...SCHEDULER_QUERY_KEYS.all, "history", jobId] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all scheduler jobs
 */
export function useSchedulerJobs() {
    return useQuery({
        queryKey: SCHEDULER_QUERY_KEYS.list(),
        queryFn: async (): Promise<SchedulerJob[]> => {
            return await apiGet<SchedulerJob[]>("/admin/scheduler/jobs");
        },
        refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds
    });
}

/**
 * Fetch a single scheduler job
 */
export function useSchedulerJob(jobId: string) {
    return useQuery({
        queryKey: SCHEDULER_QUERY_KEYS.detail(jobId),
        queryFn: async (): Promise<SchedulerJob> => {
            return await apiGet<SchedulerJob>(`/admin/scheduler/jobs/${jobId}`);
        },
        enabled: !!jobId,
    });
}

/**
 * Fetch job run history
 */
export function useJobHistory(jobId: string, limit: number = 20) {
    return useQuery({
        queryKey: SCHEDULER_QUERY_KEYS.history(jobId),
        queryFn: async (): Promise<JobRun[]> => {
            return await apiGet<JobRun[]>(`/admin/scheduler/jobs/${jobId}/history`, {
                params: { limit },
            });
        },
        enabled: !!jobId,
    });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Update job configuration
 */
export function useUpdateJob() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ jobId, data }: { jobId: string; data: UpdateJobRequest }): Promise<SchedulerJob> => {
            return await apiPut<SchedulerJob>(`/admin/scheduler/jobs/${jobId}`, data);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: SCHEDULER_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: SCHEDULER_QUERY_KEYS.detail(variables.jobId) });
        },
    });
}

/**
 * Enable a scheduler job
 */
export function useEnableJob() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (jobId: string): Promise<SchedulerJob> => {
            return await apiPost<SchedulerJob>(`/admin/scheduler/jobs/${jobId}/enable`);
        },
        onSuccess: (_data, jobId) => {
            queryClient.invalidateQueries({ queryKey: SCHEDULER_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: SCHEDULER_QUERY_KEYS.detail(jobId) });
        },
    });
}

/**
 * Disable a scheduler job
 */
export function useDisableJob() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (jobId: string): Promise<SchedulerJob> => {
            return await apiPost<SchedulerJob>(`/admin/scheduler/jobs/${jobId}/disable`);
        },
        onSuccess: (_data, jobId) => {
            queryClient.invalidateQueries({ queryKey: SCHEDULER_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: SCHEDULER_QUERY_KEYS.detail(jobId) });
        },
    });
}

/**
 * Toggle job enabled/disabled state
 */
export function useToggleJob() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ jobId, enabled }: { jobId: string; enabled: boolean }): Promise<SchedulerJob> => {
            const endpoint = enabled ? "enable" : "disable";
            return await apiPost<SchedulerJob>(`/admin/scheduler/jobs/${jobId}/${endpoint}`);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: SCHEDULER_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: SCHEDULER_QUERY_KEYS.detail(variables.jobId) });
        },
    });
}

/**
 * Run a job immediately
 */
export function useRunJobNow() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (jobId: string): Promise<JobRunResponse> => {
            return await apiPost<JobRunResponse>(`/admin/scheduler/jobs/${jobId}/run`);
        },
        onSuccess: (_data, jobId) => {
            // Invalidate to show updated last_run info
            queryClient.invalidateQueries({ queryKey: SCHEDULER_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: SCHEDULER_QUERY_KEYS.detail(jobId) });
            queryClient.invalidateQueries({ queryKey: SCHEDULER_QUERY_KEYS.history(jobId) });
        },
    });
}
